import { useTranslation } from 'next-i18next';
import { logger } from '@zep/core-logger';
import { DialogBody, DialogHeader, ResponsiveDialog } from '@/ui_v3/components/dialog';
import { AchievementStandardSearch } from '../../achievement-standard-search/components/AchievementStandardSearch';
import { useSearchStandards } from '../../achievement-standard-search/hooks/useSearchStandards';
import { MAX_SELECTION } from '../hooks/useStandardSelection';
import { useStandardSelection } from '../hooks/useStandardSelection';
import type { AchievementStandardSearchModalProps } from '../types';
import { StandardPickerDialogFooter } from './StandardPickerDialogFooter';

/**
 * 성취기준 검색 모달 컴포넌트
 *
 * 학년/과목 필터와 키워드로 성취기준을 검색하고 선택할 수 있습니다.
 *
 * StandardPickerCategoryProvider context에서 categoryContext와 subjectTree를 가져옵니다.
 */
export const AchievementStandardSearchModal = ({
  onClose,
  textPrompt: _textPrompt,
  onConfirm,
  alreadySelectedAchievementStandards
}: AchievementStandardSearchModalProps) => {
  const {
    t
  } = useTranslation(['build']);

  // 선택 상태 관리
  const {
    selectedAchievementStandards,
    toggleAchievementStandard,
    isMaxReached
  } = useStandardSelection({
    alreadySelectedAchievementStandards
  });

  // 검색 API
  const {
    search,
    isLoading: isSearching,
    searchResults
  } = useSearchStandards();

  /**
   * 성취기준 선택 완료 처리
   * 검색 출처 통계를 수집하고 상위 컴포넌트로 전달합니다.
   */
  const handleConfirm = () => {
    onConfirm(selectedAchievementStandards);
    logger.track('select_ai_std', {
      from_search_count: selectedAchievementStandards.length,
      total_count: selectedAchievementStandards.length
    });
  };
  return <ResponsiveDialog onClose={onClose} size="lg" data-sentry-element="ResponsiveDialog" data-sentry-component="AchievementStandardSearchModal" data-sentry-source-file="AchievementStandardSearchModal.tsx">
      {/* 헤더 */}
      <DialogHeader title={t('build:build.standard.modal.title')} hasCloseButton onClose={onClose} data-sentry-element="DialogHeader" data-sentry-source-file="AchievementStandardSearchModal.tsx" />

      {/* 바디 */}
      <DialogBody className="flex h-[502px] flex-col overflow-hidden p-0" data-sentry-element="DialogBody" data-sentry-source-file="AchievementStandardSearchModal.tsx">
        <AchievementStandardSearch searchResults={searchResults} isLoading={isSearching} selectedAchievementStandards={selectedAchievementStandards} onToggle={achievementStandard => toggleAchievementStandard({
        ...achievementStandard,
        origin: 'search'
      })} onSearch={search} isMaxReached={isMaxReached} data-sentry-element="AchievementStandardSearch" data-sentry-source-file="AchievementStandardSearchModal.tsx" />
      </DialogBody>

      {/* 푸터 */}
      <StandardPickerDialogFooter selectedCount={selectedAchievementStandards.length} maxCount={MAX_SELECTION} onConfirm={handleConfirm} disabled={selectedAchievementStandards.length === 0} data-sentry-element="StandardPickerDialogFooter" data-sentry-source-file="AchievementStandardSearchModal.tsx" />
    </ResponsiveDialog>;
};